import { useLoader } from 'src/provider/LoaderProvider';
import { downloadZip } from 'src/utils/downloadZip';
import type { KBFile } from 'src/utils/fileUtils';
import type { FileDownloadDetails } from './useKnowledgeBaseData';

interface UseKnowledgeBaseExportParams {
  files: KBFile[];
  knowledgeBaseName?: string;
  fetchFileById: (fileId: string) => Promise<FileDownloadDetails>;
}

export const useKnowledgeBaseExport = ({
  files,
  knowledgeBaseName,
  fetchFileById,
}: UseKnowledgeBaseExportParams) => {
  const loader = useLoader();

  const exportAllFiles = async (
    successCallback?: () => void,
    errorCallback?: (message: string) => void,
  ) => {
    try {
      loader.show({ progress: true });

      const exportableFiles = files.filter((file) => {
        if (file.type === 'folder') return false;
        if (file.collector_id) return false;
        if (file.state !== 'uploaded') return false;

        return true;
      });

      // Resolve file details with at most 1 fetchFileById call running at a time.
      const CONCURRENCY = 1;
      const details = new Array<FileDownloadDetails>(exportableFiles.length);
      let nextIndex = 0;
      let completed = 0;

      const worker = async () => {
        while (nextIndex < exportableFiles.length) {
          const currentIndex = nextIndex++;

          details[currentIndex] = await fetchFileById(exportableFiles[currentIndex].id as string);
          completed++;
          loader.updateProgress(Math.round((completed / exportableFiles.length) * 100));
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, exportableFiles.length) }, () => worker()),
      );

      const downloadableFiles = details.filter(
        (file) => Boolean(file?.url) && Boolean(file?.filename),
      );

      if (!downloadableFiles.length) {
        errorCallback?.('Couldn’t find any files with download urls');
        return;
      }

      await downloadZip(
        downloadableFiles,
        `${knowledgeBaseName ?? 'Knowledge Base'} Files.zip`,
        loader,
      );
      successCallback?.();
    } catch (error) {
      console.error(error);
    } finally {
      loader.hide();
    }
  };

  return { exportAllFiles };
};
